import {
  formatUnits,
  parseUnits,
  readContract,
  writeContractAndWait,
  zeroAddress,
} from "../viem";
import { isNativeFamilyCashBankLogType } from "../../constants/familyCashBankLog";
import type { FamilyCashBankLogEntry } from "../../constants/familyCashBankLog";
import MafiaFamilyCashBankAbi from "../../abis/MafiaFamilyCashBank.json";
import config from "../../config/config";

const GAME_CASH_DECIMALS = 18;
const NATIVE_DECIMALS = 18;

const address = (config as { familyCashBankAddress?: string })
  .familyCashBankAddress as string;
const abi = MafiaFamilyCashBankAbi;

export type FamilyCashBankLogOnchain = FamilyCashBankLogEntry;

/** In-game cash held by the family (wei → number). */
export const getFamilyGameCashBalance = async (
  familyId: number
): Promise<number> => {
  const wei = (await readContract({
    address,
    abi,
    functionName: "familyBalance",
    args: [familyId],
  })) as string;
  return Number(formatUnits(wei || "0", GAME_CASH_DECIMALS));
};

/** Native coin held by the family treasury (wei → number). */
export const getFamilyNativeBalance = async (
  familyId: number
): Promise<number> => {
  const wei = (await readContract({
    address,
    abi,
    functionName: "familyNativeBalance",
    args: [familyId],
  })) as string;
  return Number(formatUnits(wei || "0", NATIVE_DECIMALS));
};

export const depositFamilyGameCash = async (
  account: string,
  familyId: number,
  amountCash: number
) => {
  const amountWei = parseUnits(
    String(Math.floor(amountCash)),
    GAME_CASH_DECIMALS
  );
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "deposit",
    args: [familyId, amountWei],
    account,
  });
  return data;
};

export const withdrawFamilyGameCash = async (
  account: string,
  familyId: number,
  amountCash: number
) => {
  const amountWei = parseUnits(
    String(Math.floor(amountCash)),
    GAME_CASH_DECIMALS
  );
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "withdraw",
    args: [familyId, amountWei],
    account,
  });
  return data;
};

export const withdrawFamilyNative = async (
  account: string,
  familyId: number,
  amountNative: number,
  recipient = account
) => {
  const amountWei = parseUnits(String(amountNative), NATIVE_DECIMALS);
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "withdrawNative",
    args: [familyId, recipient, amountWei],
    account,
  });
  return data;
};

export const swapFamilyNativeToCash = async (
  account: string,
  familyId: number,
  amountNative: number
) => {
  const amountWei = parseUnits(String(amountNative), NATIVE_DECIMALS);
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "swapNativeToCash",
    args: [familyId, amountWei],
    account,
  });
  return data;
};

export const getMaxPromotionRank = async (): Promise<number> => {
  const max = (await readContract({
    address,
    abi,
    functionName: "MAX_RANK",
  })) as number | string;
  return Number(max);
};

/** Per-rank promotion reward in cash (index 0 = rank 1, …). */
export const getFamilyPromotionRewards = async (
  familyId: number
): Promise<number[]> => {
  const res = (await readContract({
    address,
    abi,
    functionName: "getPromotionRewards",
    args: [familyId],
  })) as any;
  const list = (Array.isArray(res) ? res : res?.rewards ?? []) as string[];
  return list.map((wei) =>
    Number(formatUnits(wei || "0", GAME_CASH_DECIMALS))
  );
};

export const setFamilyPromotionRewards = async (
  account: string,
  familyId: number,
  ranks: number[],
  amountsCash: number[]
) => {
  const amountsWei = amountsCash.map((amount) =>
    parseUnits(String(Math.floor(amount)), GAME_CASH_DECIMALS)
  );
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "setPromotionRewards",
    args: [familyId, ranks, amountsWei],
    account,
  });
  return data;
};

export const getFamilyLogsCount = async (familyId: number): Promise<number> => {
  const count = (await readContract({
    address,
    abi,
    functionName: "getFamilyLogsCount",
    args: [familyId],
  })) as number | string;
  return Number(count);
};

const parseBankLog = (x: any): FamilyCashBankLogOnchain => {
  const logType = Number(x?.logType ?? x?.[0] ?? 0);
  const logSubType = String(x?.logSubType ?? x?.[1] ?? "");
  const user = (x?.user ?? x?.[2] ?? zeroAddress) as string;
  const recipient = (x?.recipient ?? x?.[3] ?? zeroAddress) as string;
  const amountWei = (x?.amount ?? x?.[4] ?? "0") as string;
  const secondaryAmountWei = (x?.secondaryAmount ?? x?.[5] ?? "0") as string;
  const rank = Number(x?.rank ?? x?.[6] ?? 0);
  const timestamp = Number(x?.timestamp ?? x?.[7] ?? 0);
  const primaryDecimals = isNativeFamilyCashBankLogType(logType)
    ? NATIVE_DECIMALS
    : GAME_CASH_DECIMALS;

  return {
    logType,
    logSubType,
    user,
    recipient,
    amount: Number(formatUnits(amountWei || "0", primaryDecimals)),
    secondaryAmount: Number(
      formatUnits(secondaryAmountWei || "0", GAME_CASH_DECIMALS)
    ),
    rank,
    timestamp,
  };
};

export const getFamilyLogs = async (
  familyId: number,
  start: number,
  length: number
): Promise<FamilyCashBankLogOnchain[]> => {
  if (!length) return [];
  const res = (await readContract({
    address,
    abi,
    functionName: "getFamilyLogs",
    args: [familyId, start, length],
  })) as any;
  const list = (Array.isArray(res) ? res : res?.list ?? []) as any[];
  return list.map(parseBankLog);
};

const FETCH_LOGS_BATCH_SIZE = 50;

/** Loads the full on-chain log list for client-side tab filtering and pagination. */
export const fetchAllFamilyCashBankLogs = async (
  familyId: number
): Promise<FamilyCashBankLogOnchain[]> => {
  const total = await getFamilyLogsCount(familyId);
  if (total <= 0) return [];

  const all: FamilyCashBankLogOnchain[] = [];
  for (let start = 0; start < total; start += FETCH_LOGS_BATCH_SIZE) {
    const length = Math.min(FETCH_LOGS_BATCH_SIZE, total - start);
    const batch = await getFamilyLogs(familyId, start, length);
    all.push(...batch);
  }

  return all;
};
